import React from 'react';
import { Box, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import {
  History as HistoryIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  RemoveCircleOutline as WriteOffIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { formatDate } from '../../../utils/formatDate';
import {
  InventoryBlockHistoryAction,
  InventoryBlockHistoryContext,
  InventoryBlockHistoryEntry,
} from '../../../types/inventory-block-history.types';
import {
  InfoCard,
  CardHeader,
  CardTitle,
  CardContent,
  infoCardHeaderSx,
  infoCardTitleSx,
} from './InventoryDetailModal.styles';

export interface InventoryBlockHistorySectionProps {
  entries: InventoryBlockHistoryEntry[];
  isLoading: boolean;
  error: string | null;
}

const getActionLabel = (action: InventoryBlockHistoryAction): string => {
  if (action === 'BLOCK') return 'Bloqueada';
  if (action === 'UNBLOCK') return 'Desbloqueada';
  return 'Dada de baja';
};

const getContextLabel = (context?: InventoryBlockHistoryContext): string | undefined => {
  if (context === 'WAREHOUSE') return 'Almacén';
  if (context === 'BRANCH') return 'Sucursal';
  return undefined;
};

const formatUserName = (user?: { firstName: string; lastName: string }): string => {
  if (!user) return 'Usuario desconocido';
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || 'Usuario desconocido';
};

const InventoryBlockHistorySection: React.FC<InventoryBlockHistorySectionProps> = ({
  entries,
  isLoading,
  error,
}) => {
  const theme = useTheme();

  const getActionChipSx = (action: InventoryBlockHistoryAction) => {
    if (action === 'WRITE_OFF') {
      const main = theme.palette.text.secondary;
      const dark = theme.palette.mode === 'dark';
      return {
        bgcolor: alpha(main, dark ? 0.22 : 0.12),
        color: dark ? theme.palette.grey[300] : theme.palette.grey[700],
        fontWeight: 600,
      };
    }
    const isBlock = action === 'BLOCK';
    const main = isBlock ? theme.palette.warning.main : theme.palette.success.main;
    const dark = theme.palette.mode === 'dark';
    return {
      bgcolor: alpha(main, dark ? 0.22 : 0.12),
      color: dark ? main : isBlock ? theme.palette.warning.dark : theme.palette.success.dark,
      fontWeight: 600,
    };
  };

  return (
    <InfoCard sx={{ mt: 3 }}>
      <CardHeader sx={(t) => infoCardHeaderSx(t, 'rgb(234, 88, 12)')}>
        <CardTitle sx={(t) => infoCardTitleSx(t, '#ea580c')}>
          <HistoryIcon fontSize="small" style={{ marginRight: '8px' }} />
          Historial de bloqueos y bajas
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoading && error && (
          <Alert severity="warning" sx={{ mb: 0 }}>
            {error}
          </Alert>
        )}

        {!isLoading && !error && entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No hay registros de bloqueos ni bajas para esta pieza.
          </Typography>
        )}

        {!isLoading && !error && entries.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {entries.map((entry, index) => {
              const isLast = index === entries.length - 1;
              const contextLabel = getContextLabel(entry.context);

              return (
                <Box
                  key={entry.id}
                  sx={{
                    py: 2,
                    borderBottom: isLast ? 0 : 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        icon={
                          entry.action === 'BLOCK' ? (
                            <LockIcon sx={{ fontSize: '14px !important' }} />
                          ) : entry.action === 'UNBLOCK' ? (
                            <LockOpenIcon sx={{ fontSize: '14px !important' }} />
                          ) : (
                            <WriteOffIcon sx={{ fontSize: '14px !important' }} />
                          )
                        }
                        label={getActionLabel(entry.action)}
                        sx={getActionChipSx(entry.action)}
                      />
                      {contextLabel && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={contextLabel}
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                      {entry.action === 'WRITE_OFF' && entry.sessionCode && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Conteo: ${entry.sessionCode}`}
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatDate(entry.performedAt)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.75 }}
                  >
                    {formatUserName(entry.performedBy)}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: entry.comment ? 'text.primary' : 'text.secondary',
                      fontStyle: entry.comment ? 'normal' : 'italic',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {entry.comment?.trim() ? entry.comment.trim() : 'Sin comentario'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </InfoCard>
  );
};

export default InventoryBlockHistorySection;
